import { useState } from 'react'
import { api } from '../api/client'
import { useLoadable, ErrorBlock, EmptyBlock, Pill } from '../components/ui'
import type { RegisterProposal } from '../types'

/* Owner Centre R1-1 — the owner register. Anyone can propose who owns a folder
   (from Owners); a named register approver confirms it here. Nobody approves a
   change they proposed or that names them. Approving changes who the Owners page
   shows and where access requests go; it changes nothing in Google. */

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

const who = (p: { name: string; email: string } | null) => (p ? `${p.name} (${p.email})` : 'nobody')

function Proposal({ p, onDone }: { p: RegisterProposal; onDone: (u: RegisterProposal) => void }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(true); setError(null)
    try {
      const { proposal } = await api.decideProposal(p.proposalId, decision, note)
      onDone(proposal)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that.')
    } finally {
      setBusy(false)
    }
  }
  const open = p.status === 'proposed'
  return (
    <div className="block" style={{ borderLeft: open && p.canDecide ? '3px solid var(--accent)' : '3px solid var(--rule)', marginBottom: 'var(--s-md)' }}>
      <p className="eyebrow" style={{ marginBottom: 'var(--s-xs)' }}>{p.driveName}</p>
      <h3 style={{ marginTop: 0 }}>{p.folderName}</h3>
      <p style={{ marginBottom: 'var(--s-xs)' }}>
        Owner: <b>{who(p.after.owner)}</b>{p.after.deputy && <> · Deputy: <b>{who(p.after.deputy)}</b></>}
      </p>
      <p className="note" style={{ margin: 0 }}>
        Now: {p.before.status === 'missing' ? 'no owner' : `${who(p.before.owner)} (${p.before.status})`}
      </p>
      <p className="note" style={{ marginTop: 0 }}>
        Proposed by {p.proposedBy} on {when(p.proposedAt)}. Why: {p.reason}
      </p>

      {!open && (
        <p style={{ marginBottom: 0 }}>
          <Pill tone="neutral">{p.status === 'approved' ? 'Approved' : 'Rejected'}</Pill>{' '}
          by {p.decidedBy}{p.decidedAt && ` on ${when(p.decidedAt)}`}{p.decisionNote && `: ${p.decisionNote}`}
        </p>
      )}

      {open && p.canDecide && (
        <div style={{ display: 'grid', gap: 'var(--s-sm)', maxWidth: 560 }}>
          <label htmlFor={`dn-${p.proposalId}`}>Note (optional)</label>
          <input className="field" id={`dn-${p.proposalId}`} value={note} onChange={(e) => setNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
            <button className="btn btn--primary" disabled={busy} onClick={() => decide('approve')}>Approve</button>
            <button className="btn btn--secondary" disabled={busy} onClick={() => decide('reject')}>Reject</button>
          </div>
        </div>
      )}
      {open && !p.canDecide && <p style={{ marginBottom: 0 }}><Pill>Waiting for another approver</Pill></p>}
      {error && <p role="alert" style={{ marginBottom: 0 }}><Pill tone="red">Not recorded</Pill> {error}</p>}
    </div>
  )
}

export default function RegisterRoute() {
  const [data, retry] = useLoadable<{ isApprover: boolean; items: RegisterProposal[] }>(
    () => api.getProposals(), (d) => d.items.length === 0, [],
  )
  const [updates, setUpdates] = useState<Record<string, RegisterProposal>>({})
  if (data.status === 'error') return <ErrorBlock message={data.message} correlationId={data.correlationId} onRetry={retry} />

  const items = data.status === 'ready' ? data.data.items.map((p) => updates[p.proposalId] ?? p) : []
  const open = items.filter((p) => p.status === 'proposed')
  const done = items.filter((p) => p.status !== 'proposed')
  const approver = data.status === 'ready' && data.data.isApprover

  return (
    <>
      <p className="eyebrow">Owner register</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>
        {data.status === 'ready' ? (open.length ? `${open.length} owner change${open.length > 1 ? 's' : ''} waiting` : 'No owner changes waiting') : 'Owner changes'}
      </h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        {approver
          ? 'You confirm who owns each folder. Approving changes who the Owners page shows and where access requests go. It changes nothing in Google. You cannot approve a change you proposed or one that names you.'
          : 'Owner changes you proposed. A register approver confirms each one.'}
      </p>

      {data.status === 'loading' && (
        <div aria-busy="true">{[0, 1].map((i) => <div className="skeleton" key={i} style={{ height: '7rem', marginBottom: 12 }} />)}</div>
      )}
      {data.status === 'empty' && (
        <EmptyBlock title="No owner changes yet">
          Propose an owner from the Owners page: look for folders that say Owner assignment required.
        </EmptyBlock>
      )}
      {open.map((p) => <Proposal key={p.proposalId} p={p} onDone={(u) => setUpdates((m) => ({ ...m, [u.proposalId]: u }))} />)}
      {done.length > 0 && (
        <>
          <h2 style={{ marginTop: 'var(--s-xl)' }}>Decided</h2>
          {done.map((p) => <Proposal key={p.proposalId} p={p} onDone={() => {}} />)}
        </>
      )}
    </>
  )
}
